import type { RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';

const ROUTES_WITHOUT_USER = new Set(['login', 'register', 'password.request', 'reset-password', 'verify-email', 'logout']);

/** A route that needs no signed-in user. Loading the user there could loop through redirects. */
function isPublicRoute(route: RouteLocationNormalized): boolean {
  return !route.meta.requiresAuth || (typeof route.name === 'string' && ROUTES_WITHOUT_USER.has(route.name));
}

/**
 * The login page, remembering where the user was going. LoginPage reads
 * `?redirect` on success, so share-view links and other deep links survive
 * the trip through login.
 */
function loginFor(route: RouteLocationNormalized): RouteLocationRaw {
  const redirectTarget = route.fullPath;
  if (!redirectTarget || redirectTarget === '/') {
    return { name: 'login' };
  }
  return { name: 'login', query: { redirect: redirectTarget } };
}

/** Registers the guards that load the signed-in user and send everyone else to login. */
export function installAuthGuards(router: Router): void {
  let backgroundRefresh: Promise<void> | null = null;

  /**
   * Fetches the user again without holding up the navigation. Fast clicks share
   * one request. If the session has gone, or the email is no longer verified,
   * the user is sent on from whatever page they are on by then.
   */
  function refreshUserInBackground() {
    if (backgroundRefresh) {
      return;
    }
    const authStore = useAuthStore();
    backgroundRefresh = authStore
      .loadUser()
      .then(() => {
        const currentRoute = router.currentRoute.value;
        if (isPublicRoute(currentRoute)) {
          return;
        }
        if (!authStore.isVerified) {
          router.replace({ name: 'verify-email' });
        } else if (!authStore.isAuthenticated) {
          router.replace(loginFor(currentRoute));
        }
      })
      .finally(() => {
        backgroundRefresh = null;
      });
  }

  router.beforeEach(async (to: RouteLocationNormalized) => {
    if (isPublicRoute(to)) {
      return;
    }
    const authStore = useAuthStore();
    // Once a user is loaded, a click goes straight through, and the user is
    // fetched again in the background. Only the first navigation waits for
    // /api/user. So a change to the user's permissions takes effect one
    // navigation later than it used to: the navigation that fetches it is
    // judged on the permissions loaded before.
    if (authStore.userData) {
      refreshUserInBackground();
      return;
    }
    await authStore.loadUser();
    // Read after loadUser, which is what sets it.
    if (!authStore.isVerified && to.name !== 'verify-email' && to.name !== 'logout') {
      return { name: 'verify-email' };
    }
  });

  router.beforeEach((to: RouteLocationNormalized) => {
    const { isAuthenticated } = useAuthStore();
    if (!to.meta.requiresAuth || isAuthenticated) {
      return;
    }
    return loginFor(to);
  });
}
